var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useEffect, useMemo } from "react";
import { Controller, useFormContext, useWatch, } from "react-hook-form";
import { Box, Button, FormControl, RadioGroup, FormControlLabel, Radio, FormHelperText, } from "@mui/material";
import { buildFieldValidation } from "../../validators/field-components";
import { getElementValueKey, reorderChoices } from "../../../../utils/helpers";
import CommentArea from "../common/CommentArea";
import { useElementConditions, useFormLogic, } from "../../context/FormLogicContext";
import Header from "../common/Header";
import { getCommentFieldName, getInitialValueForElement, resolveStoreOthersAsComment, resolveTextUpdateMode, } from "../../../../utils/forms-functions";
import { NONE_VALUE, OTHER_VALUE } from "./constants/special-options";
import OtherOptionInput from "./OtherOptionInput";
import { useClearInvisibleValue } from "../../hooks/useClearInvisibleValue";
import { useChoicesConditions } from "../../hooks/useChoicesConditions";
var RadioGroupField = function (_a) {
    var element = _a.element, errors = _a.errors;
    var _b = useFormLogic(), globalTextUpdateMode = _b.textUpdateMode, globalStoreOthersAsComment = _b.storeOthersAsComment, globalConstraints = _b.globalConstraints;
    var _c = useFormContext(), unregister = _c.unregister, getValues = _c.getValues, setValue = _c.setValue, trigger = _c.trigger;
    var valueKey = getElementValueKey(element);
    var error = errors[valueKey];
    var _d = useElementConditions(element), isVisible = _d.isVisible, isEnabled = _d.isEnabled, isInteractive = _d.isInteractive, fieldRequired = _d.isRequired;
    var _e = useChoicesConditions(element), choicesVisible = _e.choicesVisible, choicesEnabled = _e.choicesEnabled;
    var validation = useMemo(function () {
        return buildFieldValidation(element, {
            isRequired: fieldRequired,
            readOnly: !isEnabled,
        });
    }, [element, fieldRequired, isEnabled]);
    var textUpdateMode = resolveTextUpdateMode(element, {
        textUpdateMode: globalTextUpdateMode,
    });
    var storeOthersAsComment = resolveStoreOthersAsComment(element, globalStoreOthersAsComment);
    var commentKey = getCommentFieldName(valueKey);
    var commentValue = useWatch({ name: commentKey });
    var initialValue = getInitialValueForElement(element);
    var baseChoices = useMemo(function () {
        var rawChoices = (element.choices || []);
        var choicesFormatted = rawChoices.map(function (choice) {
            if (typeof choice === "string") {
                return { value: choice, text: choice };
            }
            return choice;
        });
        if (element.choicesOrder) {
            return reorderChoices(choicesFormatted, element.choicesOrder);
        }
        return choicesFormatted;
    }, [element]);
    useEffect(function () {
        if (!choicesVisible) {
            var currentValue_1 = getValues(valueKey);
            var isBaseChoiceSelected = baseChoices.some(function (c) { return c.value === currentValue_1; });
            if (isBaseChoiceSelected) {
                setValue(valueKey, "");
            }
        }
    }, [choicesVisible, baseChoices, valueKey, getValues, setValue]);
    var isOtherSelectedValue = function (value) {
        if (!element.showOtherItem || !value)
            return false;
        if (value === OTHER_VALUE)
            return true;
        if (value === NONE_VALUE)
            return false;
        if (storeOthersAsComment)
            return false;
        return !baseChoices.some(function (choice) { return choice.value === value; });
    };
    var readOtherText = function (value) {
        if (!isOtherSelectedValue(value))
            return "";
        if (storeOthersAsComment)
            return String(commentValue !== null && commentValue !== void 0 ? commentValue : "");
        return value === OTHER_VALUE ? "" : String(value);
    };
    var readOtherTextLive = function (value) {
        var _a;
        if (!isOtherSelectedValue(value))
            return "";
        if (storeOthersAsComment)
            return String((_a = getValues(commentKey)) !== null && _a !== void 0 ? _a : "");
        return value === OTHER_VALUE ? "" : String(value);
    };
    var commitOtherText = function (text, onChange) {
        if (storeOthersAsComment) {
            setValue(commentKey, text, { shouldDirty: true });
            void trigger(valueKey);
            onChange(OTHER_VALUE);
            return;
        }
        onChange(text.length > 0 ? text : OTHER_VALUE);
    };
    var clearOtherText = function () {
        if (storeOthersAsComment && commentValue) {
            setValue(commentKey, "", { shouldDirty: true });
        }
    };
    var valueKeys = useMemo(function () {
        return element.showCommentArea || storeOthersAsComment
            ? [valueKey, commentKey]
            : [valueKey];
    }, [valueKey, commentKey, element.showCommentArea, storeOthersAsComment]);
    useClearInvisibleValue({
        isVisible: isVisible,
        valueKeys: valueKeys,
        unregister: unregister,
    });
    return (_jsx(_Fragment, { children: isVisible ? (_jsx(Box, __assign({ className: "component-wrapper" }, { children: _jsxs(FormControl, __assign({ component: "fieldset", error: !!error, fullWidth: true, sx: { mb: 3, position: "relative" } }, { children: [_jsx(Header, { element: element, error: !!error, isRequired: fieldRequired }), _jsx(Controller, { name: valueKey, defaultValue: initialValue, rules: {
                            validate: function (value) {
                                var _a;
                                var result = validation.safeParse(value);
                                if (!result.success) {
                                    return ((_a = result.error.errors[0]) === null || _a === void 0 ? void 0 : _a.message) || "Valor inválido";
                                }
                                if (isOtherSelectedValue(value) &&
                                    !readOtherTextLive(value)) {
                                    return (element.otherErrorText ||
                                        "Por favor, insira o outro valor.");
                                }
                                return true;
                            },
                        }, render: function (_a) {
                            var _b;
                            var field = _a.field;
                            var isOtherSelected = isOtherSelectedValue(field.value);
                            var committedOtherText = readOtherText(field.value);
                            return (_jsxs(_Fragment, { children: [element.showClearButton && (_jsx(Button, __assign({ variant: "outlined", size: "small", sx: {
                                            position: "absolute",
                                            top: 0,
                                            right: 0,
                                            zIndex: 1,
                                        }, disabled: !isInteractive || !field.value, onClick: function () {
                                            clearOtherText();
                                            field.onChange("");
                                        } }, { children: "Limpar" }))), _jsxs(RadioGroup, __assign({}, field, { value: isOtherSelected ? OTHER_VALUE : (_b = field.value) !== null && _b !== void 0 ? _b : "", row: false, onChange: function (e) {
                                            var newValue = e.target.value;
                                            clearOtherText();
                                            field.onChange(newValue);
                                        } }, { children: [choicesVisible &&
                                                baseChoices.map(function (choice, index) { return (_jsx(FormControlLabel, { value: choice.value, control: _jsx(Radio, { disabled: !isInteractive || !choicesEnabled }), label: choice.text, sx: {
                                                        marginBottom: 2,
                                                    } }, index)); }), element.showNoneItem && (_jsx(FormControlLabel, { value: NONE_VALUE, control: _jsx(Radio, { disabled: !isInteractive }), label: element.noneText || "Nenhum", sx: { marginBottom: 2 } })), element.showOtherItem && (_jsxs(_Fragment, { children: [_jsx(FormControlLabel, { value: OTHER_VALUE, control: _jsx(Radio, { disabled: !isInteractive }), label: element.otherText || "Outro" }), isOtherSelected && (_jsx(OtherOptionInput, { committedValue: committedOtherText, textUpdateMode: textUpdateMode, isEnabled: isInteractive, label: element.otherText || "Outro", placeholder: element.otherPlaceholder || "Digite aqui...", maxLength: globalConstraints.maxOthersLength, sx: { mt: 2 }, onCommit: function (text) {
                                                            return commitOtherText(text, field.onChange);
                                                        } }))] }))] })), error && (_jsx(FormHelperText, { children: error.message })), element.showCommentArea && (_jsx(CommentArea, { element: element, disabled: !isInteractive }))] }));
                        } })] })) }))) : null }));
};
export default RadioGroupField;
